/**
 * Pairwise sequence alignment — Needleman–Wunsch (global) +
 * Smith–Waterman (local). Pure-TS; suitable for sequences up to
 * ~50kb. WASM-bound minimap2 is the v0.3 large-sequence path.
 */

export interface AlignmentScores {
  match: number;
  mismatch: number;
  /** Linear gap penalty (negative). v0.1 uses linear; affine is v0.3. */
  gap: number;
}

export const DEFAULT_NT_SCORES: AlignmentScores = { match: 1, mismatch: -1, gap: -2 };

export interface AlignmentResult {
  score: number;
  /** Aligned query (with '-' gaps). */
  alignedA: string;
  /** Aligned target (with '-' gaps). */
  alignedB: string;
  /** Identity fraction over the aligned region. */
  identity: number;
}

/** Global alignment via Needleman–Wunsch. O(MN) time + memory. */
export function needlemanWunsch(
  a: string, b: string, scores: AlignmentScores = DEFAULT_NT_SCORES,
): AlignmentResult {
  const m = a.length, n = b.length;
  // Score matrix.
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i]![0] = i * scores.gap;
  for (let j = 0; j <= n; j++) dp[0]![j] = j * scores.gap;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const s = a[i - 1] === b[j - 1] ? scores.match : scores.mismatch;
      dp[i]![j] = Math.max(
        dp[i - 1]![j - 1]! + s,
        dp[i - 1]![j]! + scores.gap,
        dp[i]![j - 1]! + scores.gap,
      );
    }
  }
  // Traceback.
  let i = m, j = n;
  let aA = '', aB = '';
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0) {
      const s = a[i - 1] === b[j - 1] ? scores.match : scores.mismatch;
      if (dp[i]![j] === dp[i - 1]![j - 1]! + s) {
        aA = a[i - 1] + aA;
        aB = b[j - 1] + aB;
        i--; j--; continue;
      }
    }
    if (i > 0 && dp[i]![j] === dp[i - 1]![j]! + scores.gap) {
      aA = a[i - 1] + aA;
      aB = '-' + aB;
      i--; continue;
    }
    aA = '-' + aA;
    aB = b[j - 1] + aB;
    j--;
  }
  let matches = 0, aligned = 0;
  for (let k = 0; k < aA.length; k++) {
    if (aA[k] === '-' || aB[k] === '-') continue;
    aligned++;
    if (aA[k] === aB[k]) matches++;
  }
  return {
    score: dp[m]![n]!,
    alignedA: aA, alignedB: aB,
    identity: aligned === 0 ? 0 : matches / aligned,
  };
}

/** Local alignment via Smith–Waterman. */
export function smithWaterman(
  a: string, b: string, scores: AlignmentScores = DEFAULT_NT_SCORES,
): AlignmentResult {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  let best = 0, bi = 0, bj = 0;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const s = a[i - 1] === b[j - 1] ? scores.match : scores.mismatch;
      const v = Math.max(0,
        dp[i - 1]![j - 1]! + s,
        dp[i - 1]![j]! + scores.gap,
        dp[i]![j - 1]! + scores.gap,
      );
      dp[i]![j] = v;
      if (v > best) { best = v; bi = i; bj = j; }
    }
  }
  let i = bi, j = bj;
  let aA = '', aB = '';
  while (i > 0 && j > 0 && dp[i]![j]! > 0) {
    const s = a[i - 1] === b[j - 1] ? scores.match : scores.mismatch;
    if (dp[i]![j] === dp[i - 1]![j - 1]! + s) {
      aA = a[i - 1] + aA; aB = b[j - 1] + aB; i--; j--;
    } else if (dp[i]![j] === dp[i - 1]![j]! + scores.gap) {
      aA = a[i - 1] + aA; aB = '-' + aB; i--;
    } else {
      aA = '-' + aA; aB = b[j - 1] + aB; j--;
    }
  }
  let matches = 0, aligned = 0;
  for (let k = 0; k < aA.length; k++) {
    if (aA[k] === '-' || aB[k] === '-') continue;
    aligned++;
    if (aA[k] === aB[k]) matches++;
  }
  return {
    score: best, alignedA: aA, alignedB: aB,
    identity: aligned === 0 ? 0 : matches / aligned,
  };
}
